import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Link as LinkIcon, Upload, X, ShieldCheck, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';

const NEWS_CATEGORIES = [
  'Sustainability', 'Logistics', 'Supply Chain', 'Net Zero', 'Circular Economy',
  'Green Technology', 'Policy & Regulation', 'Industry News', 'Community Update',
  'Research & Reports', 'Events', 'Other',
];

const MAX_PDF_MB = 10;
const MAX_IMAGE_MB = 5;
const MAX_DOCS = 5;

/**
 * Member-facing dialog to submit a news article for admin review.
 * Required: title, summary, permission-confirmation checkbox, AND at least one
 * of (URL, full article body, PDF/document upload).
 */
export default function SubmitNewsDialog({ open, onClose, onSubmitted }) {
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [url, setUrl] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [docFiles, setDocFiles] = useState([]);
  const [permissionConfirmed, setPermissionConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTitle(''); setSummary(''); setContent(''); setCategory(''); setUrl('');
    setPdfFile(null); setImageFile(null); setImagePreview('');
    setDocFiles([]); setPermissionConfirmed(false); setSubmitting(false);
  };

  const handleClose = () => { reset(); onClose?.(); };

  const handlePdfChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      toast.error('Only PDF files are accepted');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_PDF_MB * 1024 * 1024) {
      toast.error(`PDF must be under ${MAX_PDF_MB}MB`);
      e.target.value = '';
      return;
    }
    setPdfFile(file);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      toast.error(`Image must be under ${MAX_IMAGE_MB}MB`);
      e.target.value = '';
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleDocChange = (e) => {
    const files = Array.from(e.target.files || []);
    setDocFiles(prev => [...prev, ...files].slice(0, MAX_DOCS));
    e.target.value = '';
  };

  const removeDoc = (idx) => {
    setDocFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const canSubmit = (
    title.trim() &&
    summary.trim() &&
    (url.trim() || pdfFile || content.trim() || docFiles.length > 0) &&
    permissionConfirmed &&
    !submitting
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('summary', summary.trim());
      formData.append('content', content.trim());
      formData.append('category', category || '');
      formData.append('url', url.trim());
      formData.append('permission_confirmed', 'true');
      if (pdfFile) formData.append('pdf', pdfFile);
      if (imageFile) formData.append('image', imageFile);
      docFiles.forEach((f, i) => formData.append(`doc_${i}`, f));

      // We bypass the global window.fetch interceptor (which mishandles
      // multipart bodies and causes ClientDisconnect on the server) and use
      // the real XMLHttpRequest exposed by `index.html`.
      const RealXHR = window.__realXHR || window.XMLHttpRequest;
      const token = sessionStorage.getItem('session_token');

      const data = await new Promise((resolve, reject) => {
        const xhr = new RealXHR();
        xhr.open('POST', '/api/news/submit', true);
        xhr.withCredentials = true;
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          let body = {};
          try { body = JSON.parse(xhr.responseText || '{}'); } catch (_) {}
          if (xhr.status >= 200 && xhr.status < 300) resolve(body);
          else reject(new Error(body.detail || `Submission failed (${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error('Network error — please try again'));
        xhr.ontimeout = () => reject(new Error('Request timed out — please try again'));
        xhr.timeout = 45000; // 45s — covers admin email fan-out
        xhr.send(formData);
      });
      toast.success(
        "Thanks! Your article has been sent to the admin team for review. You'll be notified once it's approved.",
        { duration: 6000 },
      );
      onSubmitted?.(data);
      handleClose();
    } catch (e) {
      toast.error(e.message || 'Failed to submit');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="submit-news-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-heading flex items-center gap-2">
            <FileText className="h-5 w-5 text-brand-navy" />
            Submit a news article
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            Recommend an article you've found valuable. The admin team will
            review it and publish it on the News page if appropriate.
          </p>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label htmlFor="news-submit-title" className="text-sm font-medium">
              Article title <span className="text-rose-500">*</span>
            </Label>
            <Input
              id="news-submit-title"
              data-testid="news-submit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. UK to introduce zero-emission HGV mandate by 2030"
              className="mt-1"
              maxLength={200}
            />
          </div>

          <div>
            <Label className="text-sm font-medium">
              Short summary <span className="text-rose-500">*</span>
            </Label>
            <div className="mt-1">
              <RichTextEditor
                value={summary}
                onChange={setSummary}
                placeholder="Why is this article relevant to the community? (2–3 sentences)"
                testId="news-submit-summary"
                minHeight="5rem"
              />
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">
              Full article body <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <div className="mt-1">
              <RichTextEditor
                value={content}
                onChange={setContent}
                placeholder="Paste the full article body here so members can read it without leaving BriteHub. URLs will unfurl into rich preview cards."
                testId="news-submit-content"
                minHeight="10rem"
              />
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">
              Featured image <span className="text-slate-400 font-normal">(optional, max {MAX_IMAGE_MB}MB)</span>
            </Label>
            {imagePreview ? (
              <div className="mt-1 relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid="news-submit-image-preview">
                <img src={imagePreview} alt="preview" className="w-full max-h-56 object-cover" />
                <button
                  type="button"
                  onClick={() => { setImageFile(null); setImagePreview(''); }}
                  data-testid="news-submit-image-clear"
                  className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 hover:bg-white text-slate-700 shadow flex items-center justify-center"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <Input
                id="news-submit-image"
                data-testid="news-submit-image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-brand-navy/10 file:text-brand-navy hover:file:bg-brand-navy/20 file:cursor-pointer"
              />
            )}
          </div>

          <div>
            <Label className="text-sm font-medium">Category (optional)</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1" data-testid="news-submit-category">
                <SelectValue placeholder="Choose a topic" />
              </SelectTrigger>
              <SelectContent>
                {NEWS_CATEGORIES.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Add sources <em className="text-slate-400 normal-case font-normal">(URL, PDF, or additional documents)</em>
            </p>

            <div>
              <Label htmlFor="news-submit-url" className="text-xs flex items-center gap-1.5 text-slate-600">
                <LinkIcon className="h-3.5 w-3.5" /> Article URL
              </Label>
              <Input
                id="news-submit-url"
                data-testid="news-submit-url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/article"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-xs flex items-center gap-1.5 text-slate-600">
                <Upload className="h-3.5 w-3.5" /> PDF upload (max {MAX_PDF_MB}MB)
              </Label>
              {pdfFile ? (
                <div className="mt-1 flex items-center justify-between bg-white border border-slate-200 rounded-md px-3 py-2">
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <FileText className="h-4 w-4 text-rose-500 flex-shrink-0" />
                    <span className="truncate" data-testid="news-submit-pdf-filename">{pdfFile.name}</span>
                    <span className="text-xs text-slate-400 whitespace-nowrap">
                      ({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPdfFile(null)}
                    data-testid="news-submit-pdf-clear"
                    className="text-slate-400 hover:text-rose-500 p-1"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <Input
                  id="news-submit-pdf"
                  data-testid="news-submit-pdf"
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={handlePdfChange}
                  className="mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-brand-navy/10 file:text-brand-navy hover:file:bg-brand-navy/20 file:cursor-pointer"
                />
              )}
            </div>

            <div>
              <Label className="text-xs flex items-center gap-1.5 text-slate-600">
                <Paperclip className="h-3.5 w-3.5" /> Additional documents (up to {MAX_DOCS}, 10MB each)
              </Label>
              <Input
                data-testid="news-submit-docs"
                type="file"
                multiple
                onChange={handleDocChange}
                disabled={docFiles.length >= MAX_DOCS}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,image/*"
                className="mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-brand-navy/10 file:text-brand-navy hover:file:bg-brand-navy/20 file:cursor-pointer"
              />
              {docFiles.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {docFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-white border border-slate-200 rounded-md px-3 py-1.5" data-testid={`news-submit-doc-${i}`}>
                      <div className="flex items-center gap-2 text-xs min-w-0">
                        <FileText className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                        <span className="truncate">{f.name}</span>
                        <span className="text-slate-400 whitespace-nowrap">({(f.size / 1024).toFixed(0)} KB)</span>
                      </div>
                      <button type="button" onClick={() => removeDoc(i)} className="text-slate-400 hover:text-rose-500 p-1" data-testid={`news-submit-doc-remove-${i}`}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <label
            className="flex items-start gap-3 bg-brand-sage/10 border border-brand-sage/30 rounded-lg p-4 cursor-pointer"
            data-testid="news-submit-permission-label"
          >
            <Checkbox
              checked={permissionConfirmed}
              onCheckedChange={(v) => setPermissionConfirmed(!!v)}
              data-testid="news-submit-permission-checkbox"
              className="mt-0.5"
            />
            <div className="text-sm text-slate-700 leading-relaxed">
              <ShieldCheck className="h-4 w-4 text-brand-sage inline mr-1 -mt-0.5" />
              <strong>I confirm I have permission</strong> for this news article
              to be shared on the SLL BriteHub Community platform.
            </div>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-slate-100">
          <Button variant="ghost" onClick={handleClose} data-testid="news-submit-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="news-submit-confirm"
            className="bg-gradient-to-r from-brand-navy to-brand-sage text-white"
          >
            {submitting ? 'Submitting…' : 'Submit for review'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
